/**
 * Phantom (Bedrock numbers, normal difficulty): 20 HP, undead, burns in daylight,
 * 6 damage swoop. It circles above the player (radius ~12, 15 blocks up) and
 * every few seconds dives at them, then climbs back. Drops 0-1 phantom
 * membrane when the player killed it.
 *
 * Spawning (vanilla PhantomSpawner): the player must have gone 3 in-game days
 * (72000 ticks) without sleeping or dying; at night, with sky access above sea
 * level; each attempt (60-120 s apart) succeeds with probability
 * (rest - 72000) / rest and spawns 1..(1 + difficulty) phantoms 20-34 blocks above.
 * `EntityHost.restTicks()` supplies the player's rest counter
 * (PlayerStats.timeSinceRest, reset on sleeping and on death).
 */

import { I } from '../../game/itemIds';
import { stackOf, type Stack } from '../../game/types';
import { Mob, type MobCtx } from '../mob';
import { newSteer, steerFlying } from '../nav/navigator';

export const PHANTOM_REST_TICKS = 72000;
const STEER = newSteer();

/** Probability an attempt succeeds for a player who last rested `rest` ticks ago. */
export function phantomChance(rest: number): number {
  return rest < PHANTOM_REST_TICKS ? 0 : (rest - PHANTOM_REST_TICKS) / Math.max(1, rest);
}

export class Phantom extends Mob {
  private mode: 'circle' | 'swoop' = 'circle';
  private angle: number;
  private modeT = 0;
  private attackCd = 0;
  private hasTarget = false;
  private cx = 0; private cz = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'phantom', x, y, z, { width: 0.9, height: 0.5, eye: 0.3, health: 20, speed: 0.4 }, rnd);
    this.noFallDamage = true;
    this.angle = rnd() * Math.PI * 2;
    this.cx = x; this.cz = z;
    this.modeT = 100 + Math.floor(rnd() * 100);
  }

  protected override floats(): boolean { return false; }
  override onHurtBy(): void { this.retaliate = 200; }
  xpValue(): number { return 5; }

  private fly(vx: number, vy: number, vz: number, k = 0.12): void {
    this.vx += (vx - this.vx) * k;
    this.vz += (vz - this.vz) * k;
    this.vy += (vy - this.vy) * k;
    this.vy += 0.08 * 0.98; // cancel Mob's gravity for this tick
  }

  protected ai(ctx: MobCtx): void {
    this.sunBurn(ctx);
    if (this.attackCd > 0) this.attackCd--;
    const p = ctx.host.player;
    const ok = ctx.targetable();
    const dx0 = p.x - this.x, dz0 = p.z - this.z;
    this.hasTarget = ok && dx0 * dx0 + dz0 * dz0 < 64 * 64;
    if (!this.hasTarget) {
      // drift in a lazy circle where it is
      this.angle += 0.03;
      this.glide(this.cx + Math.cos(this.angle) * 10, this.y, this.cz + Math.sin(this.angle) * 10, 0.08);
      return;
    }
    this.aggressive = this.mode === 'swoop';
    if (this.mode === 'circle') {
      this.angle += 0.045;
      const tx = p.x + Math.cos(this.angle) * 12, tz = p.z + Math.sin(this.angle) * 12, ty = p.y + 15;
      this.glide(tx, ty, tz, 0.16);
      if (--this.modeT <= 0 && this.y > p.y + 5) { this.mode = 'swoop'; this.modeT = 120; }
    } else {
      this.glide(p.x, p.y + 0.9, p.z, 0.55, true);
      if (this.attackCd === 0 && Math.hypot(p.x - this.x, p.y + 0.9 - this.y, p.z - this.z) < 1.8) {
        this.attackCd = 20;
        this.swing();
        ctx.hurtPlayer(this, 6, 0.4);
        this.endSwoop(ctx);
      } else if (--this.modeT <= 0 || (this.y <= p.y + 0.4 && this.attackCd === 0 && this.collidedH)) this.endSwoop(ctx);
    }
    this.cx = this.x; this.cz = this.z;
  }

  private endSwoop(ctx: MobCtx): void {
    this.mode = 'circle';
    this.modeT = 100 + Math.floor(ctx.host.rnd() * 100);
    this.vy = Math.max(this.vy, 0.25);
  }

  private glide(tx: number, ty: number, tz: number, speed: number, direct = false): void {
    if (direct) {
      const dx = tx - this.x, dy = ty - this.y, dz = tz - this.z, l = Math.hypot(dx, dy, dz) || 1;
      STEER.dx = dx / l; STEER.dy = dy / l; STEER.dz = dz / l;
    } else steerFlying(this.w, this.x, this.y, this.z, tx, ty, tz, 4, STEER);
    this.fly(STEER.dx * speed, STEER.dy * speed, STEER.dz * speed);
    const h = Math.hypot(STEER.dx, STEER.dz);
    if (h > 0.05) this.faceToward(this.x + STEER.dx, this.z + STEER.dz);
    this.pitch = -Math.atan2(STEER.dy, h || 1) * 0.6;
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    return byPlayer && rnd() < 0.5 ? [stackOf(I.PHANTOM_MEMBRANE, 1)] : [];
  }
}

/** Spawn rule helper for the generic Spawner: returns how many to spawn this attempt (0 = none). */
export function phantomSpawnCount(rest: number, night: boolean, difficulty: 'peaceful' | 'easy' | 'normal' | 'hard', rnd: () => number): number {
  if (!night || difficulty === 'peaceful') return 0;
  if (rnd() >= phantomChance(rest)) return 0;
  const d = difficulty === 'easy' ? 1 : difficulty === 'normal' ? 2 : 3;
  return 1 + Math.floor(rnd() * (d + 1));
}
